import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { API_ENDPOINTS } from "config/api";

// @mui material components
import Card from "@mui/material/Card";
import Container from "@mui/material/Container";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

function ForgotPassword() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [step, setStep] = useState(1); // 1: username, 2: new password
  const [formData, setFormData] = useState({
    username: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleToggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    console.log('🔵 [FORGOT PASSWORD] Form submitted');
    console.log('🔵 [FORGOT PASSWORD] Username entered:', formData.username);

    if (!formData.username) {
      console.log('🔴 [FORGOT PASSWORD] Validation failed: Username is empty');
      setMessage({ type: "error", text: "Username is required!" });
      return;
    }

    setLoading(true);
    console.log('🔵 [FORGOT PASSWORD] Loading state set to true');

    try {
      console.log('🔵 [FORGOT PASSWORD] Preparing API call to:', API_ENDPOINTS.FORGOT_PASSWORD);
      console.log('🔵 [FORGOT PASSWORD] Request payload:', {
        username: formData.username
      });

      // Call API to request password reset link
      const response = await fetch(API_ENDPOINTS.FORGOT_PASSWORD, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: formData.username,
        }),
      });

      console.log('🔵 [FORGOT PASSWORD] Response received');
      console.log('🔵 [FORGOT PASSWORD] Response status:', response.status);
      console.log('🔵 [FORGOT PASSWORD] Response ok:', response.ok);

      const result = await response.json();
      console.log('🔵 [FORGOT PASSWORD] Response data:', result);

      if (result.success) {
        console.log('✅ [FORGOT PASSWORD] Success! Password reset link sent');
        // Show success message
        setMessage({
          type: "success",
          text: "Password reset link has been sent to your email!"
        });
      } else {
        console.log('🔴 [FORGOT PASSWORD] Failed:', result.message);
        setMessage({ type: "error", text: result.message });
      }
    } catch (error) {
      console.error('❌ [FORGOT PASSWORD] Error occurred:', error);
      console.error('❌ [FORGOT PASSWORD] Error message:', error.message);
      console.error('❌ [FORGOT PASSWORD] Error stack:', error.stack);
      setMessage({ type: "error", text: "Something went wrong. Please try again." });
    }

    setLoading(false);
    console.log('🔵 [FORGOT PASSWORD] Loading state set to false');
  };

  return (
    <>
      {/* Mobile-only CSS */}
      <style>
        {`
          @media (min-width: 768px) {
            .mobile-only-button {
              display: none !important;
            }
          }
        `}
      </style>

      <MDBox
        sx={{
          minHeight: "100vh",
          width: "100vw",
          background: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Container maxWidth="sm">
          <Card
            sx={{
              background: "#ffffff",
              borderRadius: "12px",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              border: "1px solid #e2e8f0",
              maxWidth: "400px",
              margin: "0 auto",
            }}
          >
            <MDBox p={4}>
              {/* Back Button - Mobile Only */}
              <div style={{
                display: "flex",
                justifyContent: "flex-start",
                marginBottom: "24px",
                width: "100%",
              }}
                className="mobile-only-button"
              >
                <Link
                  to="/authentication/sign-in"
                  style={{
                    textDecoration: "none",
                    display: "block"
                  }}
                >
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#1f93ff",
                    cursor: "pointer",
                    padding: "12px 16px",
                    borderRadius: "8px",
                    border: "2px solid #1f93ff",
                    backgroundColor: "#f8fafc",
                    minHeight: "48px",
                    fontSize: "16px",
                    fontWeight: "600",
                  }}>
                    <ArrowBackIcon sx={{ fontSize: "20px", color: "#1f93ff" }} />
                    <span style={{ color: "#1f93ff", fontSize: "16px", fontWeight: "600" }}>
                      Back to Login
                    </span>
                  </div>
                </Link>
              </div>

              {/* Header */}
              <MDBox textAlign="left" mb={4}>
                <MDTypography
                  variant="h4"
                  sx={{
                    color: "#1f2937",
                    fontWeight: "600",
                    fontSize: "1.875rem",
                    mb: 2,
                  }}
                >
                  Reset password
                </MDTypography>
                <MDTypography
                  variant="body2"
                  sx={{
                    color: "#6b7280",
                    fontSize: "0.875rem",
                    lineHeight: 1.5,
                  }}
                >
                  Enter your username to get the password reset instructions
                </MDTypography>
              </MDBox>

              {message.text && (
                <MDBox mb={3}>
                  <Alert severity={message.type}>{message.text}</Alert>
                </MDBox>
              )}

              {/* Form */}
              <MDBox component="form" onSubmit={handleSubmit}>
                <MDBox mb={4}>
                  <MDInput
                    type="text"
                    placeholder=""
                    fullWidth
                    value={formData.username}
                    onChange={handleInputChange("username")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "6px",
                        border: "1px solid #d1d5db",
                        backgroundColor: "#ffffff",
                        fontSize: "0.875rem",
                        "& fieldset": {
                          border: "none",
                        },
                        "&:hover": {
                          borderColor: "#9ca3af",
                        },
                        "&:hover fieldset": {
                          border: "none",
                        },
                        "&.Mui-focused": {
                          borderColor: "#1f93ff",
                        },
                        "&.Mui-focused fieldset": {
                          border: "none",
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: "12px 16px",
                        fontSize: "0.875rem",
                        color: "#374151",
                        "&::placeholder": {
                          color: "#9ca3af",
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </MDBox>

                {/* Submit Button */}
                <MDBox mb={4}>
                  <MDButton
                    type="submit"
                    fullWidth
                    size="large"
                    disabled={loading}
                    sx={{
                      background: "#1f93ff",
                      borderRadius: "6px",
                      textTransform: "none",
                      fontSize: "0.875rem",
                      fontWeight: "500",
                      py: 1.5,
                      color: "#ffffff",
                      boxShadow: "none",
                      "&:hover": {
                        background: "#1e40af",
                        boxShadow: "none",
                      },
                    }}
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </MDButton>
                </MDBox>

                {/* Back to Login Link */}
                <MDBox textAlign="center">
                  <MDTypography variant="body2" sx={{ fontSize: "0.875rem" }}>
                    If you want to go back to the login page,{" "}
                    <Link
                      to="/authentication/sign-in"
                      style={{
                        color: "#1f93ff",
                        textDecoration: "none",
                        fontWeight: "500",
                      }}
                    >
                      click here
                    </Link>
                    .
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>
        </Container>
      </MDBox>
    </>
  );
}

export default ForgotPassword;
